import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Switch, FormControlLabel,
    Tab, Tabs, Box, Typography, Chip, Slider,
    MenuItem, Select, InputLabel, FormControl,
    CircularProgress, IconButton, InputAdornment,
    Paper, List, ListItem, ListItemText, ListItemSecondaryAction,
    Divider, Tooltip, Checkbox, ListItemIcon, Avatar,
} from "@material-ui/core";
import {
    Visibility, VisibilityOff, DeleteOutline, Add, Send,
    CloudUpload, Memory, Edit,
} from "@material-ui/icons";
import { AutoAwesome } from "@mui/icons-material";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import PlaybookTab from "../PlaybookTab";

const useStyles = makeStyles((theme) => ({
    dialog: { "& .MuiDialog-paper": { borderRadius: 4, maxWidth: 700, width: "100%" } },
    dialogTitle: {
        background: "linear-gradient(135deg, #F97316 0%, #FB923C 100%)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "16px 24px",
    },
    tabs: {
        borderBottom: "1px solid #e5e7eb",
        "& .MuiTab-root": { textTransform: "none", minWidth: 80, fontWeight: 600 },
        "& .Mui-selected": { color: "#F97316" },
        "& .MuiTabs-indicator": { background: "#F97316" },
    },
    tabPanel: { padding: theme.spacing(2, 0) },
    sectionLabel: {
        fontSize: "0.7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        marginBottom: 8,
        letterSpacing: "0.05em",
    },
    cardGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 8,
        marginBottom: 16,
        "@media (max-width:500px)": { gridTemplateColumns: "1fr" },
    },
    cardOption: {
        border: "1px solid #e5e7eb",
        borderRadius: 4,
        padding: "10px 14px",
        cursor: "pointer",
        transition: "all 0.15s",
        "&:hover": { borderColor: "#F97316" },
    },
    cardOptionSelected: {
        border: "1px solid #F97316",
        backgroundColor: theme.palette?.mode === "light" || theme.mode === "light" ? "#f9f9f9" : "#273142",
        borderRadius: 4,
        padding: "10px 14px",
        cursor: "pointer",
        color: theme.palette?.mode === "light" || theme.mode === "light" ? "#000" : "#fff",
        "& $cardTitle": {
            color: theme.palette?.mode === "light" || theme.mode === "light" ? "#000" : "#fff",
        },
        "& $cardSub": {
            color: theme.palette?.mode === "light" || theme.mode === "light" ? "#4B5563" : "#d1d5db",
        }
    },
    cardTitle: { fontWeight: 600, fontSize: "0.85rem" },
    cardSub: { fontSize: "0.72rem", color: "#6B7280", marginTop: 2 },
    langChip: {
        cursor: "pointer",
        fontWeight: 600,
        "&.selected": { background: "#F97316", color: "#fff" },
    },
    sliderRow: { display: "flex", alignItems: "center", gap: theme.spacing(2) },
    saveBtn: {
        background: "linear-gradient(135deg, #F97316 0%, #FB923C 100%)",
        color: "#fff",
        fontWeight: 600,
        borderRadius: 4,
        textTransform: "none",
        "&:hover": { background: "#ea6a0a" },
    },
    knowledgeItem: {
        border: "1px solid #e5e7eb",
        borderRadius: 4,
        marginBottom: 8,
        "&:hover": { background: "#FFF7ED" },
    },
    chatBubble: {
        maxWidth: "80%",
        padding: "8px 12px",
        borderRadius: 4,
        marginBottom: 8,
        wordBreak: "break-word",
    },
    chatUser: { background: "#F97316", color: "#fff", marginLeft: "auto", borderRadius: "12px 12px 0 12px" },
    chatBot: { background: "#F3F4F6", color: "#111", borderRadius: "12px 12px 12px 0" },
}));

const LANGS = ["BR", "US", "ES", "FR", "DE", "IT", "Auto"];

const defaultAgent = {
    name: "",
    role: "general",
    voiceTone: "friendly",
    model: "gpt-4o-mini",
    apiKey: "",
    languages: JSON.stringify(["BR"]),
    personality: "",
    isActive: true,
    initialMessage: "",
    responseRules: "",
    allowTransfer: false,
    transferKeywords: "",
    transferMessage: "",
    transferUserIds: JSON.stringify([]),
    transferUserMode: "random",
    returnMenuEnabled: false,
    returnMenuKeywords: "menu, menu inicial, voltar ao menu, voltar para o menu, ir para o menu, inicio",
    returnMenuMessage: "",
    closeTicketEnabled: false,
    closeTicketKeywords: "sair, encerrar, finalizar, tchau, encerrar atendimento",
    closeTicketMessage: "",
    responseDelay: 1,
    maxChars: null,
    humanizeConversation: false,
    audioEnabled: false,
    temperature: 1,
    maxTokens: 2000,
    maxMessages: 10,
    queueId: null,
    replyMode: "all",
    replyContactIds: JSON.stringify([]),
    replyTagIds: JSON.stringify([]),
    replyKeywords: JSON.stringify([]),
};

const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index} style={{ padding: "16px 0" }}>
        {value === index && children}
    </div>
);

const AIAgentModal = ({ open, onClose, agentId }) => {
    const classes = useStyles();
    const theme = useTheme();

    const FUNCTIONS = [
        { value: "general", label: i18n.t("aiAgentModal.functions.general"), sub: i18n.t("aiAgentModal.functions.generalSub") },
        { value: "sales", label: i18n.t("aiAgentModal.functions.sales"), sub: i18n.t("aiAgentModal.functions.salesSub") },
        { value: "support", label: i18n.t("aiAgentModal.functions.support"), sub: i18n.t("aiAgentModal.functions.supportSub") },
        { value: "scheduling", label: i18n.t("aiAgentModal.functions.scheduling"), sub: i18n.t("aiAgentModal.functions.schedulingSub") },
        { value: "financial", label: i18n.t("aiAgentModal.functions.financial"), sub: i18n.t("aiAgentModal.functions.financialSub") },
    ];

    const TONES = [
        { value: "friendly", label: i18n.t("aiAgentModal.tones.friendly") },
        { value: "professional", label: i18n.t("aiAgentModal.tones.professional") },
        { value: "formal", label: i18n.t("aiAgentModal.tones.formal") },
        { value: "casual", label: i18n.t("aiAgentModal.tones.casual") },
        { value: "empathetic", label: i18n.t("aiAgentModal.tones.empathetic") },
        { value: "technical", label: i18n.t("aiAgentModal.tones.technical") },
    ];

    const MODELS = [
        { value: "gpt-4o-mini", label: "GPT-4o Mini", sub: i18n.t("aiAgentModal.models.recommended") },
        { value: "gpt-4o", label: "GPT-4o", sub: i18n.t("aiAgentModal.models.smarter") },
        { value: "gpt-4.1-mini", label: "GPT-4.1 Mini", sub: i18n.t("aiAgentModal.models.fast") },
        { value: "gpt-4.1", label: "GPT-4.1", sub: i18n.t("aiAgentModal.models.highPerf") },
        { value: "gpt-4.1-nano", label: "GPT-4.1 Nano", sub: i18n.t("aiAgentModal.models.ultraFast") },
        { value: "o4-mini", label: "O4 Mini", sub: i18n.t("aiAgentModal.models.reasoning") },
    ];
    const [tab, setTab] = useState(0);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showKey, setShowKey] = useState(false);
    const [agent, setAgent] = useState(defaultAgent);
    const [savedAgent, setSavedAgent] = useState(null);
    const [queues, setQueues] = useState([]);
    const [users, setUsers] = useState([]);
    const [knowledge, setKnowledge] = useState([]);
    const [newKnowledge, setNewKnowledge] = useState({ type: "text", title: "", content: "" });
    const [addingKnowledge, setAddingKnowledge] = useState(false);
    const [editingKnowledge, setEditingKnowledge] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [testMessages, setTestMessages] = useState([]);
    const [testInput, setTestInput] = useState("");
    const [testLoading, setTestLoading] = useState(false);
    const chatEndRef = useRef(null);

    // Aba Responder
    const [tags, setTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [replyContacts, setReplyContacts] = useState([]);
    const [replyContactSearch, setReplyContactSearch] = useState("");
    const [loadingReplyContacts, setLoadingReplyContacts] = useState(false);

    const langs = agent.languages ? JSON.parse(agent.languages) : ["BR"];

    const replyContactIdsParsed = (() => { try { return JSON.parse(agent.replyContactIds || "[]"); } catch { return []; } })();
    const replyTagIdsParsed = (() => { try { return JSON.parse(agent.replyTagIds || "[]"); } catch { return []; } })();

    const loadReplyContacts = async (search = "") => {
        setLoadingReplyContacts(true);
        try {
            const { data } = await api.get("/contacts", { params: { searchParam: search, pageNumber: 1 } });
            setReplyContacts(data?.contacts || data || []);
        } catch { }
        setLoadingReplyContacts(false);
    };

    const toggleReplyContact = (id) => {
        const next = replyContactIdsParsed.includes(id)
            ? replyContactIdsParsed.filter((i) => i !== id)
            : [...replyContactIdsParsed, id];
        setVal("replyContactIds", JSON.stringify(next));
    };

    const toggleReplyTag = (id) => {
        const next = replyTagIdsParsed.includes(id)
            ? replyTagIdsParsed.filter((i) => i !== id)
            : [...replyTagIdsParsed, id];
        setVal("replyTagIds", JSON.stringify(next));
    };

    useEffect(() => {
        if (!open) {
            setTab(0);
            setAgent(defaultAgent);
            setSavedAgent(null);
            setKnowledge([]);
            setTestMessages([]);
            return;
        }
        api.get("/queue").then((r) => setQueues(r.data)).catch(() => { });
        api.get("/users").then((r) => setUsers(r.data?.users || r.data || [])).catch(() => { });
        setLoadingTags(true);
        api.get("/tags").then((r) => setTags(r.data?.tags || r.data || [])).catch(() => { }).finally(() => setLoadingTags(false));
        if (agentId) {
            setLoading(true);
            api.get(`/ai-agents/${agentId}`)
                .then((r) => {
                    setAgent(r.data);
                    setSavedAgent(r.data);
                    setKnowledge(r.data.knowledgeItems || []);
                })
                .catch(toastError)
                .finally(() => setLoading(false));
        }
    }, [open, agentId]);

    useEffect(() => {
        if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, [testMessages]);

    const set = (field) => (e) => { const v = e.target.value; setAgent((prev) => ({ ...prev, [field]: v })); };
    const setVal = (field, val) => setAgent((prev) => ({ ...prev, [field]: val }));

    const toggleLang = (lang) => {
        const current = agent.languages ? JSON.parse(agent.languages) : [];
        const updated = current.includes(lang) ? current.filter((l) => l !== lang) : [...current, lang];
        setVal("languages", JSON.stringify(updated.length ? updated : ["BR"]));
    };

    const handleSave = async () => {
        if (!agent.name?.trim()) { toast.error(i18n.t("aiAgentModal.toasts.nameRequired")); return; }
        if (!agent.apiKey?.trim()) { toast.error(i18n.t("aiAgentModal.toasts.apiKeyRequired")); return; }
        setSaving(true);
        try {
            let res;
            if (agentId || savedAgent?.id) {
                res = await api.put(`/ai-agents/${agentId || savedAgent.id}`, agent);
            } else {
                res = await api.post("/ai-agents", agent);
            }
            setSavedAgent(res.data);
            setAgent(res.data);
            toast.success(i18n.t("aiAgentModal.toasts.saved"));
            onClose(res.data);
        } catch (err) {
            toastError(err);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveEditKnowledge = async () => {
        if (!editingKnowledge) return;
        if (!newKnowledge.title?.trim()) { toast.error(i18n.t("aiAgentModal.toasts.titleRequired")); return; }
        if (editingKnowledge.type !== "file" && !newKnowledge.content?.trim()) { toast.error(i18n.t("aiAgentModal.toasts.contentRequired")); return; }
        try {
            const res = await api.put(`/ai-agents/${savedAgent.id}/knowledge/${editingKnowledge.id}`, {
                title: newKnowledge.title,
                content: newKnowledge.content,
            });
            setKnowledge((prev) => prev.map((k) => k.id === editingKnowledge.id ? res.data : k));
            setEditingKnowledge(null);
            setNewKnowledge({ type: "text", title: "", content: "" });
            toast.success(i18n.t("aiAgentModal.toasts.itemUpdated"));
        } catch (err) {
            toastError(err);
        }
    };

    const handleAddKnowledge = async () => {
        if (!savedAgent?.id) { toast.warning(i18n.t("aiAgentModal.toasts.saveFirst")); return; }
        if (!newKnowledge.title?.trim()) { toast.error(i18n.t("aiAgentModal.toasts.titleRequired")); return; }

        try {
            let res;
            if (newKnowledge.type === "file") {
                if (!selectedFile) { toast.error(i18n.t("aiAgentModal.toasts.fileRequired")); return; }
                const formData = new FormData();
                formData.append("file", selectedFile);
                formData.append("title", newKnowledge.title);
                formData.append("typeArch", "aiknowledge");
                res = await api.post(`/ai-agents/${savedAgent.id}/knowledge/upload`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                if (!newKnowledge.content?.trim()) { toast.error(i18n.t("aiAgentModal.toasts.contentRequired")); return; }
                res = await api.post(`/ai-agents/${savedAgent.id}/knowledge`, newKnowledge);
            }
            setKnowledge((prev) => [...prev, res.data]);
            setNewKnowledge({ type: "text", title: "", content: "" });
            setSelectedFile(null);
            setAddingKnowledge(false);
            toast.success(i18n.t("aiAgentModal.toasts.itemAdded"));
        } catch (err) {
            toastError(err);
        }
    };

    const handleDeleteKnowledge = async (kid) => {
        try {
            await api.delete(`/ai-agents/${savedAgent.id}/knowledge/${kid}`);
            setKnowledge((prev) => prev.filter((k) => k.id !== kid));
            if (editingKnowledge?.id === kid) {
                setEditingKnowledge(null);
                setNewKnowledge({ type: "text", title: "", content: "" });
            }
        } catch (err) {
            toastError(err);
        }
    };

    const handleTest = async () => {
        if (!testInput.trim()) return;
        const userMsg = testInput.trim();
        setTestInput("");
        const msgs = [...testMessages, { role: "user", content: userMsg }];
        setTestMessages(msgs);
        setTestLoading(true);
        try {
            const agentConfig = savedAgent || { ...agent, id: null };
            const res = await api.post("/ai-agent/test", {
                agentConfig,
                messages: msgs,
            });
            setTestMessages([...msgs, { role: "assistant", content: res.data.response }]);
        } catch (err) {
            toastError(err);
        } finally {
            setTestLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} className={classes.dialog} maxWidth={false}>
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Memory />
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {agentId ? i18n.t("aiAgentModal.editTitle") : i18n.t("aiAgentModal.createTitle")}
                </Typography>
            </DialogTitle>

            <DialogContent style={{ padding: "0 24px 8px", minHeight: 480 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                        <CircularProgress style={{ color: "#F97316" }} />
                    </Box>
                ) : (
                    <>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)} className={classes.tabs}>
                            <Tab label={i18n.t("aiAgentModal.tabs.basic")} />
                            <Tab label={i18n.t("aiAgentModal.tabs.behavior")} />
                            <Tab label={i18n.t("aiAgentModal.tabs.advanced")} />
                            <Tab label={i18n.t("aiAgentModal.tabs.knowledge")} />
                            <Tab label={i18n.t("aiAgentModal.tabs.test")} />
                            <Tab label={i18n.t("aiAgentModal.tabs.reply")} />
                            <Tab label="Playbook de Vendas" />
                        </Tabs>

                        {/* ===================== BÁSICO ===================== */}
                        <TabPanel value={tab} index={0}>
                            <TextField
                                label={i18n.t("aiAgentModal.basic.agentName")}
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={agent.name}
                                onChange={set("name")}
                                style={{ marginBottom: 16 }}
                            />

                            <Typography className={classes.sectionLabel}>{i18n.t("aiAgentModal.basic.functionSection")}</Typography>
                            <div className={classes.cardGrid}>
                                {FUNCTIONS.map((f) => (
                                    <div
                                        key={f.value}
                                        className={agent.role === f.value ? classes.cardOptionSelected : classes.cardOption}
                                        onClick={() => setVal("role", f.value)}
                                    >
                                        <Typography className={classes.cardTitle}>{f.label}</Typography>
                                        <Typography className={classes.cardSub}>{f.sub}</Typography>
                                    </div>
                                ))}
                            </div>

                            <Typography className={classes.sectionLabel}>{i18n.t("aiAgentModal.basic.voiceToneSection")}</Typography>
                            <div className={classes.cardGrid}>
                                {TONES.map((t) => (
                                    <div
                                        key={t.value}
                                        className={agent.voiceTone === t.value ? classes.cardOptionSelected : classes.cardOption}
                                        onClick={() => setVal("voiceTone", t.value)}
                                    >
                                        <Typography className={classes.cardTitle}>{t.label}</Typography>
                                    </div>
                                ))}
                            </div>

                            <Typography className={classes.sectionLabel}>{i18n.t("aiAgentModal.basic.aiModelSection")}</Typography>
                            <div className={classes.cardGrid}>
                                {MODELS.map((m) => (
                                    <div
                                        key={m.value}
                                        className={agent.model === m.value ? classes.cardOptionSelected : classes.cardOption}
                                        onClick={() => setVal("model", m.value)}
                                    >
                                        <Typography className={classes.cardTitle}>{m.label}</Typography>
                                        <Typography className={classes.cardSub}>{m.sub}</Typography>
                                    </div>
                                ))}
                            </div>

                            <TextField
                                label={i18n.t("aiAgentModal.basic.apiKeyLabel")}
                                fullWidth
                                variant="outlined"
                                size="small"
                                type={showKey ? "text" : "password"}
                                value={agent.apiKey}
                                onChange={set("apiKey")}
                                style={{ marginBottom: 16, marginTop: 8 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowKey(!showKey)}>
                                                {showKey ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Typography className={classes.sectionLabel}>{i18n.t("aiAgentModal.basic.responseLanguages")}</Typography>
                            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                {LANGS.map((lang) => (
                                    <Chip
                                        key={lang}
                                        label={lang}
                                        onClick={() => toggleLang(lang)}
                                        color={langs.includes(lang) ? "primary" : "default"}
                                        style={langs.includes(lang) ? { background: "#F97316", color: "#fff" } : {}}
                                    />
                                ))}
                            </Box>

                            <TextField
                                label={i18n.t("aiAgentModal.basic.personality")}
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                size="small"
                                placeholder={i18n.t("aiAgentModal.basic.personalityPlaceholder")}
                                value={agent.personality}
                                onChange={set("personality")}
                                style={{ marginBottom: 12 }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(agent.isActive)}
                                        onChange={(e) => setVal("isActive", e.target.checked)}
                                        style={{ color: "#F97316" }}
                                    />
                                }
                                label={i18n.t("aiAgentModal.basic.activeAgent")}
                            />
                        </TabPanel>

                        {/* ===================== COMPORTAMENTO ===================== */}
                        <TabPanel value={tab} index={1}>
                            <TextField
                                label={i18n.t("aiAgentModal.behavior.initialMessage")}
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                size="small"
                                placeholder={i18n.t("aiAgentModal.behavior.initialMsgPlaceholder")}
                                value={agent.initialMessage}
                                onChange={set("initialMessage")}
                                style={{ marginBottom: 16 }}
                            />

                            <TextField
                                label={i18n.t("aiAgentModal.behavior.responseRules")}
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                size="small"
                                placeholder={i18n.t("aiAgentModal.behavior.responseRulesPlaceholder")}
                                value={agent.responseRules}
                                onChange={set("responseRules")}
                                style={{ marginBottom: 16 }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(agent.allowTransfer)}
                                        onChange={(e) => setVal("allowTransfer", e.target.checked)}
                                    />
                                }
                                label={i18n.t("aiAgentModal.behavior.allowTransfer")}
                                style={{ marginBottom: 8 }}
                            />

                            {agent.allowTransfer && (
                                <>
                                    <TextField
                                        label={i18n.t("aiAgentModal.behavior.transferKeywords")}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        placeholder={i18n.t("aiAgentModal.behavior.transferKwPlaceholder")}
                                        value={agent.transferKeywords}
                                        onChange={set("transferKeywords")}
                                        style={{ marginBottom: 12 }}
                                    />
                                    <TextField
                                        label={i18n.t("aiAgentModal.behavior.transferMessage")}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        placeholder={i18n.t("aiAgentModal.behavior.transferMsgPlaceholder")}
                                        value={agent.transferMessage}
                                        onChange={set("transferMessage")}
                                        style={{ marginBottom: 12 }}
                                    />

                                    {/* Modo de atribuição de atendente */}
                                    <Typography style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", color: "#6B7280", marginBottom: 8, letterSpacing: "0.05em" }}>
                                        {i18n.t("aiAgentModal.behavior.responsibleAgent")}
                                    </Typography>
                                    <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 12 }}>
                                        <InputLabel>{i18n.t("aiAgentModal.behavior.assignMode")}</InputLabel>
                                        <Select
                                            value={agent.transferUserMode || "random"}
                                            onChange={(e) => setVal("transferUserMode", e.target.value)}
                                            label={i18n.t("aiAgentModal.behavior.assignMode")}
                                        >
                                            <MenuItem value="random">{i18n.t("aiAgentModal.behavior.randomAgent")}</MenuItem>
                                            <MenuItem value="specific">{i18n.t("aiAgentModal.behavior.specificAgent")}</MenuItem>
                                        </Select>
                                    </FormControl>

                                    {agent.transferUserMode === "specific" && (
                                        <Paper variant="outlined" style={{ maxHeight: 180, overflowY: "auto", borderRadius: 4, marginBottom: 12 }}>
                                            {users.length === 0 ? (
                                                <Typography style={{ padding: 12, fontSize: 13, color: "#9CA3AF" }}>{i18n.t("aiAgentModal.behavior.noAgentFound")}</Typography>
                                            ) : (
                                                <List dense disablePadding>
                                                    {users.map((u) => {
                                                        const selectedIds = (() => { try { return JSON.parse(agent.transferUserIds || "[]"); } catch { return []; } })();
                                                        const checked = selectedIds.includes(u.id);
                                                        const toggle = () => {
                                                            const next = checked
                                                                ? selectedIds.filter((id) => id !== u.id)
                                                                : [...selectedIds, u.id];
                                                            setVal("transferUserIds", JSON.stringify(next));
                                                        };
                                                        return (
                                                            <ListItem key={u.id} button onClick={toggle} style={{ paddingTop: 4, paddingBottom: 4 }}>
                                                                <ListItemIcon style={{ minWidth: 36 }}>
                                                                    <Checkbox
                                                                        edge="start"
                                                                        checked={checked}
                                                                        size="small"
                                                                        style={{ color: checked ? "#F97316" : undefined, padding: 4 }}
                                                                    />
                                                                </ListItemIcon>
                                                                <Avatar
                                                                    style={{
                                                                        width: 26, height: 26, fontSize: 12, marginRight: 8,
                                                                        background: checked ? "#F97316" : "#E5E7EB",
                                                                        color: checked ? "#fff" : "#4B5563",
                                                                    }}
                                                                >
                                                                    {(u.name || u.email || "?")[0].toUpperCase()}
                                                                </Avatar>
                                                                <ListItemText
                                                                    primary={u.name || u.email}
                                                                    secondary={u.email && u.name ? u.email : undefined}
                                                                    primaryTypographyProps={{ style: { fontSize: 13, fontWeight: checked ? 600 : 400 } }}
                                                                    secondaryTypographyProps={{ style: { fontSize: 11 } }}
                                                                />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            )}
                                        </Paper>
                                    )}
                                    {(agent.transferUserMode === "specific") && (() => { try { return JSON.parse(agent.transferUserIds || "[]").length; } catch { return 0; } })() > 0 && (
                                        <Typography style={{ fontSize: 12, color: "#059669", marginBottom: 12 }}>
                                            ✓ {(() => { try { return JSON.parse(agent.transferUserIds || "[]").length; } catch { return 0; } })()} {i18n.t("aiAgentModal.behavior.agentsSelected")}
                                        </Typography>
                                    )}
                                </>
                            )}

                            <FormControl fullWidth variant="outlined" size="small" style={{ marginTop: 8 }}>
                                <InputLabel shrink>{i18n.t("aiAgentModal.behavior.sectorQueue")}</InputLabel>
                                <Select
                                    displayEmpty
                                    value={agent.queueId || ""}
                                    onChange={(e) => setVal("queueId", e.target.value || null)}
                                    label={i18n.t("aiAgentModal.behavior.sectorQueue")}
                                >
                                    <MenuItem value=""><em>{i18n.t("aiAgentModal.behavior.noQueue")}</em></MenuItem>
                                    {queues.map((q) => (
                                        <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* ── Retorno ao menu inicial ── */}
                            <Box style={{ marginTop: 20, paddingTop: 16, borderTop: "1px dashed #e5e7eb" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(agent.returnMenuEnabled)}
                                            onChange={(e) => setVal("returnMenuEnabled", e.target.checked)}
                                        />
                                    }
                                    label={i18n.t("aiAgentModal.behavior.returnMenuEnabled")}
                                />
                                {agent.returnMenuEnabled && (
                                    <>
                                        <TextField
                                            label={i18n.t("aiAgentModal.behavior.returnMenuKeywords")}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder={i18n.t("aiAgentModal.behavior.returnMenuKwPlaceholder")}
                                            value={agent.returnMenuKeywords || ""}
                                            onChange={set("returnMenuKeywords")}
                                            style={{ marginTop: 12, marginBottom: 12 }}
                                        />
                                        <TextField
                                            label={i18n.t("aiAgentModal.behavior.returnMenuMessage")}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder={i18n.t("aiAgentModal.behavior.returnMenuMsgPlaceholder")}
                                            value={agent.returnMenuMessage || ""}
                                            onChange={set("returnMenuMessage")}
                                        />
                                    </>
                                )}
                            </Box>

                            {/* ── Encerrar atendimento ── */}
                            <Box style={{ marginTop: 20, paddingTop: 16, borderTop: "1px dashed #e5e7eb" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={Boolean(agent.closeTicketEnabled)}
                                            onChange={(e) => setVal("closeTicketEnabled", e.target.checked)}
                                        />
                                    }
                                    label={i18n.t("aiAgentModal.behavior.closeTicketEnabled")}
                                />
                                {agent.closeTicketEnabled && (
                                    <>
                                        <TextField
                                            label={i18n.t("aiAgentModal.behavior.closeTicketKeywords")}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            placeholder={i18n.t("aiAgentModal.behavior.closeTicketKwPlaceholder")}
                                            value={agent.closeTicketKeywords || ""}
                                            onChange={set("closeTicketKeywords")}
                                            style={{ marginTop: 12, marginBottom: 12 }}
                                        />
                                        <TextField
                                            label={i18n.t("aiAgentModal.behavior.closeTicketMessage")}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            minRows={3}
                                            placeholder={i18n.t("aiAgentModal.behavior.closeTicketMsgPlaceholder")}
                                            value={agent.closeTicketMessage || ""}
                                            onChange={set("closeTicketMessage")}
                                        />
                                    </>
                                )}
                            </Box>
                        </TabPanel>
                        <TabPanel value={tab} index={2}>
                            <Typography className={classes.sectionLabel}>{i18n.t("aiAgentModal.advanced.responseDelay")}</Typography>
                            <div className={classes.sliderRow}>
                                <Slider
                                    min={0} max={10} step={1}
                                    value={agent.responseDelay || 0}
                                    onChange={(_, v) => setVal("responseDelay", v)}
                                    style={{ color: "#F97316", flex: 1 }}
                                />
                                <Typography style={{ minWidth: 30 }}>{agent.responseDelay || 0}s</Typography>
                            </div>

                            <Typography className={classes.sectionLabel} style={{ marginTop: 16 }}>
                                {i18n.t("aiAgentModal.advanced.charLimit")}
                            </Typography>
                            <div className={classes.sliderRow}>
                                <Slider
                                    min={100} max={4000} step={100}
                                    value={agent.maxChars || 2000}
                                    onChange={(_, v) => setVal("maxChars", v)}
                                    style={{ color: "#F97316", flex: 1 }}
                                />
                                <Typography style={{ minWidth: 50 }}>{agent.maxChars || 2000}</Typography>
                            </div>

                            <Box display="flex" flexDirection="column" gap={1} mt={2}>
                                <FormControlLabel
                                    control={
                                        <Switch checked={Boolean(agent.humanizeConversation)} onChange={(e) => setVal("humanizeConversation", e.target.checked)} />
                                    }
                                    label={i18n.t("aiAgentModal.advanced.humanizeChat")}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch checked={Boolean(agent.audioEnabled)} onChange={(e) => setVal("audioEnabled", e.target.checked)} />
                                    }
                                    label={i18n.t("aiAgentModal.advanced.audioFeatures")}
                                />
                            </Box>

                            <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                                <TextField
                                    label={i18n.t("aiAgentModal.advanced.temperature")}
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ min: 0, max: 2, step: 0.1 }}
                                    value={agent.temperature ?? 1}
                                    onChange={(e) => setVal("temperature", parseFloat(e.target.value))}
                                    style={{ width: 130 }}
                                />
                                <TextField
                                    label={i18n.t("aiAgentModal.advanced.maxTokens")}
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ min: 100, max: 16000, step: 100 }}
                                    value={agent.maxTokens || 2000}
                                    onChange={(e) => setVal("maxTokens", parseInt(e.target.value))}
                                    style={{ width: 130 }}
                                />
                                <TextField
                                    label={i18n.t("aiAgentModal.advanced.maxMessages")}
                                    type="number"
                                    variant="outlined"
                                    size="small"
                                    inputProps={{ min: 1, max: 50 }}
                                    value={agent.maxMessages || 10}
                                    onChange={(e) => setVal("maxMessages", parseInt(e.target.value))}
                                    style={{ width: 140 }}
                                />
                            </Box>
                        </TabPanel>

                        {/* ===================== CONHECIMENTO ===================== */}
                        <TabPanel value={tab} index={3}>
                            {!savedAgent?.id ? (
                                <Box textAlign="center" py={4}>
                                    <AutoAwesome style={{ fontSize: 40, color: "#F97316", opacity: 0.5 }} />
                                    <Typography color="textSecondary" style={{ marginTop: 8 }}>
                                        {i18n.t("aiAgentModal.knowledge.saveFirst")}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        className={classes.saveBtn}
                                        onClick={handleSave}
                                        disabled={saving}
                                        style={{ marginTop: 12 }}
                                    >
                                        {i18n.t("aiAgentModal.knowledge.saveAgentNow")}
                                    </Button>
                                </Box>
                            ) : (
                                <>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                            {i18n.t("aiAgentModal.knowledge.baseTitle")} ({knowledge.length} {knowledge.length === 1 ? i18n.t("aiAgentModal.knowledge.item") : i18n.t("aiAgentModal.knowledge.items")})
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Add />}
                                            onClick={() => setAddingKnowledge(!addingKnowledge)}
                                            style={{ borderColor: "#F97316", color: "#F97316" }}
                                        >
                                            {i18n.t("aiAgentModal.knowledge.add")}
                                        </Button>
                                    </Box>

                                    {(addingKnowledge || editingKnowledge) && (
                                        <Paper variant="outlined" style={{ padding: 12, borderRadius: 4, marginBottom: 12 }}>
                                            <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 8 }}>
                                                <InputLabel>{i18n.t("aiAgentModal.knowledge.type")}</InputLabel>
                                                <Select
                                                    value={newKnowledge.type}
                                                    onChange={(e) => { const v = e.target.value; setNewKnowledge({ type: v, title: "", content: "" }); setSelectedFile(null); }}
                                                    label={i18n.t("aiAgentModal.knowledge.type")}
                                                    disabled={!!editingKnowledge}
                                                >
                                                    <MenuItem value="text">📝 Texto</MenuItem>
                                                    <MenuItem value="url">🔗 URL</MenuItem>
                                                    <MenuItem value="file">📎 Arquivo</MenuItem>
                                                </Select>
                                            </FormControl>

                                            <TextField
                                                label={i18n.t("aiAgentModal.knowledge.title")}
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                value={newKnowledge.title}
                                                onChange={(e) => { const v = e.target.value; setNewKnowledge((p) => ({ ...p, title: v })); }}
                                                style={{ marginBottom: 8 }}
                                            />

                                            {/* Campo texto */}
                                            {newKnowledge.type === "text" && (
                                                <TextField
                                                    label={i18n.t("aiAgentModal.knowledge.content")}
                                                    fullWidth
                                                    multiline
                                                    rows={4}
                                                    size="small"
                                                    variant="outlined"
                                                    value={newKnowledge.content}
                                                    onChange={(e) => { const v = e.target.value; setNewKnowledge((p) => ({ ...p, content: v })); }}
                                                    style={{ marginBottom: 8 }}
                                                    placeholder={i18n.t("aiAgentModal.knowledge.contentPlaceholder")}
                                                />
                                            )}

                                            {/* Campo URL */}
                                            {newKnowledge.type === "url" && (
                                                <TextField
                                                    label="URL"
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    value={newKnowledge.content}
                                                    onChange={(e) => { const v = e.target.value; setNewKnowledge((p) => ({ ...p, content: v })); }}
                                                    style={{ marginBottom: 8 }}
                                                    placeholder="https://..."
                                                    InputProps={{
                                                        startAdornment: <span style={{ marginRight: 6, color: "#6B7280" }}>🔗</span>
                                                    }}
                                                />
                                            )}

                                            {/* Campo arquivo - apenas ao adicionar (não ao editar) */}
                                            {newKnowledge.type === "file" && !editingKnowledge && (
                                                <div style={{ marginBottom: 8 }}>
                                                    <input
                                                        id="ai-knowledge-file"
                                                        type="file"
                                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.png,.jpg,.jpeg,.webp"
                                                        style={{ display: "none" }}
                                                        onChange={(e) => {
                                                            const f = e.target.files?.[0] || null;
                                                            setSelectedFile(f);
                                                            if (f && !newKnowledge.title) {
                                                                setNewKnowledge((p) => ({ ...p, title: f.name }));
                                                            }
                                                        }}
                                                    />
                                                    <label htmlFor="ai-knowledge-file">
                                                        <Paper
                                                            variant="outlined"
                                                            style={{
                                                                padding: "16px 12px",
                                                                borderRadius: 4,
                                                                cursor: "pointer",
                                                                borderStyle: "dashed",
                                                                borderColor: selectedFile ? "#F97316" : "#D1D5DB",
                                                                background: selectedFile ? "#FFF7ED" : "#F9FAFB",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 10,
                                                            }}
                                                            component="span"
                                                        >
                                                            <span style={{ fontSize: 22 }}>📎</span>
                                                            <div>
                                                                {selectedFile ? (
                                                                    <>
                                                                        <Typography style={{ fontWeight: 600, fontSize: 13, color: "#F97316" }}>{selectedFile.name}</Typography>
                                                                        <Typography style={{ fontSize: 11, color: "#6B7280" }}>{(selectedFile.size / 1024).toFixed(1)} KB</Typography>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Typography style={{ fontSize: 13, fontWeight: 500 }}>{i18n.t("aiAgentModal.knowledge.clickToSelect")}</Typography>
                                                                        <Typography style={{ fontSize: 11, color: "#9CA3AF" }}>{i18n.t("aiAgentModal.knowledge.fileTypes")}</Typography>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </Paper>
                                                    </label>
                                                </div>
                                            )}

                                            <Box display="flex" gap={1}>
                                                <Button size="small" variant="contained" className={classes.saveBtn}
                                                    onClick={editingKnowledge ? handleSaveEditKnowledge : handleAddKnowledge}>
                                                    {editingKnowledge ? i18n.t("aiAgentModal.knowledge.save") : i18n.t("aiAgentModal.knowledge.add")}
                                                </Button>
                                                <Button size="small" onClick={() => { setAddingKnowledge(false); setEditingKnowledge(null); setSelectedFile(null); setNewKnowledge({ type: "text", title: "", content: "" }); }}>{i18n.t("aiAgentModal.knowledge.cancel")}</Button>
                                            </Box>
                                        </Paper>
                                    )}

                                    {knowledge.length === 0 ? (
                                        <Typography color="textSecondary" align="center" style={{ marginTop: 24 }}>
                                            {i18n.t("aiAgentModal.knowledge.noItems")}
                                        </Typography>
                                    ) : (
                                        <List disablePadding>
                                            {knowledge.map((item) => (
                                                <React.Fragment key={item.id}>
                                                    <ListItem className={classes.knowledgeItem}>
                                                        <ListItemText
                                                            primary={item.title || `(${item.type})`}
                                                            secondary={
                                                                item.content
                                                                    ? item.content.length > 80
                                                                        ? item.content.substring(0, 80) + "..."
                                                                        : item.content
                                                                    : item.filePath
                                                            }
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <Tooltip title={i18n.t("aiAgentModal.knowledge.edit")}>
                                                                <IconButton size="small" onClick={() => {
                                                                    setEditingKnowledge(item);
                                                                    setNewKnowledge({ type: item.type, title: item.title || "", content: item.content || "" });
                                                                    setAddingKnowledge(false);
                                                                }} style={{ marginRight: 4 }}>
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={i18n.t("aiAgentModal.knowledge.delete")}>
                                                                <IconButton size="small" onClick={() => handleDeleteKnowledge(item.id)}>
                                                                    <DeleteOutline fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                </React.Fragment>
                                            ))}
                                        </List>
                                    )}
                                </>
                            )}
                        </TabPanel>

                        {/* ===================== RESPONDER ===================== */}
                        <TabPanel value={tab} index={6}>
                            <PlaybookTab />
                        </TabPanel>
                        <TabPanel value={tab} index={5}>
                            <Typography style={{ fontSize: 13, color: "#6B7280", marginBottom: 16 }}>
                                {i18n.t("aiAgentModal.reply.description")}
                            </Typography>

                            {/* Modo de resposta */}
                            <Typography className={classes.sectionLabel}>{i18n.t("aiAgentModal.reply.replyMode")}</Typography>
                            <div className={classes.cardGrid} style={{ gridTemplateColumns: "repeat(3,1fr)", marginBottom: 20 }}>
                                {[
                                    { value: "all", label: i18n.t("aiAgentModal.reply.allLabel"), icon: "🌐", sub: i18n.t("aiAgentModal.reply.allSub") },
                                    { value: "contacts", label: i18n.t("aiAgentModal.reply.contactsLabel"), icon: "👥", sub: i18n.t("aiAgentModal.reply.contactsSub") },
                                    { value: "tags", label: i18n.t("aiAgentModal.reply.tagsLabel"), icon: "🏷️", sub: i18n.t("aiAgentModal.reply.tagsSub") },
                                    { value: "keyword", label: "Palavra-chave", icon: "🔑", sub: "Ativa por palavra na mensagem" },
                                ].map((m) => (
                                    <div
                                        key={m.value}
                                        className={agent.replyMode === m.value ? classes.cardOptionSelected : classes.cardOption}
                                        onClick={() => {
                                            setVal("replyMode", m.value);
                                            if (m.value === "contacts" && replyContacts.length === 0) {
                                                loadReplyContacts("");
                                            }
                                        }}
                                    >
                                        <Typography style={{ fontSize: 20, marginBottom: 2 }}>{m.icon}</Typography>
                                        <Typography className={classes.cardTitle}>{m.label}</Typography>
                                        <Typography className={classes.cardSub}>{m.sub}</Typography>
                                    </div>
                                ))}
                            </div>

                            {agent.replyMode === "keyword" && (
                                <div style={{ marginBottom: 20 }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 6 }}>Palavras-chave de ativação</Typography>
                                    <Typography variant="caption" style={{ color: "#777", display: "block", marginBottom: 8 }}>
                                        A IA ativa quando a mensagem do contato contém qualquer uma destas palavras (ignora maiúscula/acento). Uma por linha. Defina também uma fila para o agente.
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        variant="outlined"
                                        size="small"
                                        placeholder={"orçamento\npreço\nquero comprar"}
                                        value={(() => { try { return JSON.parse(agent.replyKeywords || "[]").join("\n"); } catch { return ""; } })()}
                                        onChange={(e) => setVal("replyKeywords", JSON.stringify(e.target.value.split("\n").map((x) => x.trim()).filter(Boolean)))}
                                    />
                                </div>
                            )}

                            {/* === CONTATOS ESPECÍFICOS === */}
                            {agent.replyMode === "contacts" && (
                                <>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                            {i18n.t("aiAgentModal.reply.contactsTitle")} ({replyContactIdsParsed.length} {i18n.t("aiAgentModal.reply.selected")})
                                        </Typography>
                                        {replyContactIdsParsed.length > 0 && (
                                            <Button size="small" style={{ color: "#EF4444", textTransform: "none", fontSize: 12 }}
                                                onClick={() => setVal("replyContactIds", JSON.stringify([]))}>
                                                {i18n.t("aiAgentModal.reply.clearSelection")}
                                            </Button>
                                        )}
                                    </Box>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        placeholder={i18n.t("aiAgentModal.reply.searchPlaceholder")}
                                        value={replyContactSearch}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setReplyContactSearch(v);
                                            loadReplyContacts(v);
                                        }}
                                        style={{ marginBottom: 8 }}
                                    />
                                    <Paper variant="outlined" style={{ maxHeight: 240, overflowY: "auto", borderRadius: 4 }}>
                                        {loadingReplyContacts ? (
                                            <Box display="flex" justifyContent="center" p={2}>
                                                <CircularProgress size={20} style={{ color: "#F97316" }} />
                                            </Box>
                                        ) : replyContacts.length === 0 ? (
                                            <Typography style={{ padding: 12, fontSize: 13, color: "#9CA3AF", textAlign: "center" }}>
                                                {i18n.t("aiAgentModal.reply.noContacts")}
                                            </Typography>
                                        ) : (
                                            <List dense disablePadding>
                                                {replyContacts.map((c) => {
                                                    const checked = replyContactIdsParsed.includes(c.id);
                                                    return (
                                                        <ListItem key={c.id} button onClick={() => toggleReplyContact(c.id)} style={{ paddingTop: 4, paddingBottom: 4 }}>
                                                            <ListItemIcon style={{ minWidth: 36 }}>
                                                                <Checkbox
                                                                    edge="start"
                                                                    checked={checked}
                                                                    size="small"
                                                                    style={{ color: checked ? "#F97316" : undefined, padding: 4 }}
                                                                />
                                                            </ListItemIcon>
                                                            <Avatar style={{
                                                                width: 26, height: 26, fontSize: 12, marginRight: 8,
                                                                background: checked ? "#F97316" : "#E5E7EB",
                                                                color: checked ? "#fff" : "#4B5563",
                                                            }}>
                                                                {(c.name || c.number || "?")[0].toUpperCase()}
                                                            </Avatar>
                                                            <ListItemText
                                                                primary={c.name || c.number}
                                                                secondary={c.number && c.name ? c.number : undefined}
                                                                primaryTypographyProps={{ style: { fontSize: 13, fontWeight: checked ? 600 : 400 } }}
                                                                secondaryTypographyProps={{ style: { fontSize: 11 } }}
                                                            />
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        )}
                                    </Paper>
                                </>
                            )}

                            {/* === POR TAG === */}
                            {agent.replyMode === "tags" && (
                                <>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                            {i18n.t("aiAgentModal.reply.tagsTitle")} ({replyTagIdsParsed.length} {i18n.t("aiAgentModal.reply.selected")})
                                        </Typography>
                                        {replyTagIdsParsed.length > 0 && (
                                            <Button size="small" style={{ color: "#EF4444", textTransform: "none", fontSize: 12 }}
                                                onClick={() => setVal("replyTagIds", JSON.stringify([]))}>
                                                {i18n.t("aiAgentModal.reply.clearSelection")}
                                            </Button>
                                        )}
                                    </Box>
                                    {loadingTags ? (
                                        <Box display="flex" justifyContent="center" p={2}>
                                            <CircularProgress size={20} style={{ color: "#F97316" }} />
                                        </Box>
                                    ) : tags.length === 0 ? (
                                        <Box textAlign="center" py={3} style={{ border: "1px dashed #D1D5DB", borderRadius: 4 }}>
                                            <Typography style={{ fontSize: 13, color: "#9CA3AF" }}>{i18n.t("aiAgentModal.reply.noTags")}</Typography>
                                            <Typography style={{ fontSize: 11, color: "#C4C4C4", marginTop: 4 }}>{i18n.t("aiAgentModal.reply.createTagsHint")}</Typography>
                                        </Box>
                                    ) : (
                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                            {tags.map((tag) => {
                                                const selected = replyTagIdsParsed.includes(tag.id);
                                                return (
                                                    <Chip
                                                        key={tag.id}
                                                        label={tag.name}
                                                        onClick={() => toggleReplyTag(tag.id)}
                                                        style={{
                                                            background: selected ? (tag.color || "#F97316") : "#F3F4F6",
                                                            color: selected ? "#fff" : "#374151",
                                                            fontWeight: selected ? 700 : 400,
                                                            border: selected ? `2px solid ${tag.color || "#F97316"}` : "2px solid transparent",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                    {replyTagIdsParsed.length > 0 && (
                                        <Typography style={{ fontSize: 12, color: "#059669", marginTop: 10 }}>
                                            {replyTagIdsParsed.length === 1
                                                ? i18n.t("aiAgentModal.reply.tagFilterMsgSingle")
                                                : i18n.t("aiAgentModal.reply.tagFilterMsgMulti", { count: replyTagIdsParsed.length })}
                                        </Typography>
                                    )}
                                </>
                            )}

                            {agent.replyMode === "all" && (
                                <Box style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 4, padding: "12px 16px" }}>
                                    <Typography style={{ fontSize: 13, color: "#166534" }}>
                                        {i18n.t("aiAgentModal.reply.allModeMsg")}
                                    </Typography>
                                </Box>
                            )}
                        </TabPanel>

                        {/* ===================== TESTAR ===================== */}
                        <TabPanel value={tab} index={4}>
                            <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 12 }}>
                                <InputLabel>{i18n.t("aiAgentModal.test.modelForTest")}</InputLabel>
                                <Select
                                    value={agent.model}
                                    onChange={set("model")}
                                    label={i18n.t("aiAgentModal.test.modelForTest")}
                                >
                                    {MODELS.map((m) => (
                                        <MenuItem key={m.value} value={m.value}>
                                            {m.label} — {m.sub}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Paper
                                variant="outlined"
                                style={{
                                    minHeight: 240,
                                    maxHeight: 320,
                                    overflowY: "auto",
                                    padding: 12,
                                    borderRadius: 4,
                                    background: "#fdfdfd",
                                    marginBottom: 12,
                                    display: "flex",
                                    flexDirection: "column", backgroundColor: theme.mode === "light" ? "#f9f9f9" : "#273142",

                                }}
                            >
                                {testMessages.length === 0 && (
                                    <Typography color="textSecondary" align="center" style={{ margin: "auto", color: theme.palette?.mode === "light" || theme.mode === "light" ? "#ffffff" : "#000000", }}>
                                        {i18n.t("aiAgentModal.test.sendMsg")}
                                    </Typography>
                                )}
                                {testMessages.map((m, i) => (
                                    <Box
                                        key={i}
                                        display="flex"
                                        justifyContent={m.role === "user" ? "flex-end" : "flex-start"}
                                    >
                                        <div
                                            className={`${classes.chatBubble} ${m.role === "user" ? classes.chatUser : classes.chatBot}`}
                                        >
                                            <Typography variant="body2">{m.content}</Typography>
                                        </div>
                                    </Box>
                                ))}
                                {testLoading && (
                                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                                        <CircularProgress size={16} style={{ color: "#F97316" }} />
                                        <Typography variant="caption" color="textSecondary">{i18n.t("aiAgentModal.test.waiting")}</Typography>
                                    </Box>
                                )}
                                <div ref={chatEndRef} />
                            </Paper>

                            <Box display="flex" gap={1}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    placeholder={i18n.t("aiAgentModal.test.inputPlaceholder")}
                                    value={testInput}
                                    onChange={(e) => { const v = e.target.value; setTestInput(v); }}
                                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleTest()}
                                    disabled={testLoading}
                                />
                                <IconButton
                                    style={{ background: "#F97316", color: "#fff", borderRadius: 4 }}
                                    onClick={handleTest}
                                    disabled={testLoading || !agent.apiKey?.trim()}
                                >
                                    <Send />
                                </IconButton>
                            </Box>
                            {!agent.apiKey?.trim() && (
                                <Typography variant="caption" color="error" style={{ marginTop: 4, display: "block" }}>
                                    {i18n.t("aiAgentModal.test.fillApiKey")}
                                </Typography>
                            )}
                        </TabPanel>
                    </>
                )}
            </DialogContent>

            <DialogActions style={{ padding: "12px 24px", gap: 8 }}>
                <Button onClick={onClose} style={{ borderRadius: 4, textTransform: "none" }}>
                    {i18n.t("aiAgentModal.actions.cancel")}
                </Button>
                <Button
                    variant="contained"
                    className={classes.saveBtn}
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving && <CircularProgress size={16} style={{ color: "#fff" }} />}
                >
                    {saving ? i18n.t("aiAgentModal.actions.saving") : i18n.t("aiAgentModal.actions.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AIAgentModal;
